/*
 * Static-site form delivery via FormSubmit (https://formsubmit.co) — free, no server needed.
 * Every quote, enquiry-list and custom/OEM request is emailed to the business inbox.
 *
 * Setup (one time): after the site is live, submit any form once. FormSubmit sends an
 * activation email to the inbox below — click "Activate Form". From then on every
 * submission arrives by email. FormSubmit also gives you a random alias string; put it in
 * REACT_APP_FORMSUBMIT_ID (Cloudflare Pages → Settings → Environment variables) to keep
 * the real address out of the page source.
 */
import { EMAIL } from "../seo";

const TARGET = process.env.REACT_APP_FORMSUBMIT_ID || EMAIL;
const AJAX_URL = `https://formsubmit.co/ajax/${TARGET}`;
const POST_URL = `https://formsubmit.co/${TARGET}`;

const LABELS = {
  name: "Name",
  company: "Company",
  business_type: "Business type",
  city: "City",
  phone: "Phone / WhatsApp",
  email: "Email",
  message: "Message",
};

function baseFields(subject, form) {
  const out = {
    _subject: subject,
    _template: "table",
    _captcha: "false",
  };
  Object.entries(LABELS).forEach(([k, label]) => {
    if (form[k]) out[label] = form[k];
  });
  if (form.email) out._replyto = form.email;
  return out;
}

async function postJson(fields) {
  const res = await fetch(AJAX_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(fields),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || String(data.success) === "false") {
    throw new Error(data.message || `Form service error ${res.status}`);
  }
  return data;
}

/* File uploads go through FormSubmit's standard multipart endpoint (the AJAX endpoint is
   JSON-only), posted into a hidden iframe so the visitor stays on the page. */
function postMultipart(fields, file) {
  return new Promise((resolve, reject) => {
    const name = `fs-${Date.now()}`;
    const iframe = document.createElement("iframe");
    iframe.name = name;
    iframe.title = "form delivery";
    iframe.style.display = "none";
    const form = document.createElement("form");
    form.action = POST_URL;
    form.method = "POST";
    form.enctype = "multipart/form-data";
    form.target = name;
    form.style.display = "none";

    Object.entries({ ...fields, _next: window.location.origin + "/thanks.html" }).forEach(([k, v]) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = k;
      input.value = v;
      form.appendChild(input);
    });
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.name = "attachment";
    const dt = new DataTransfer();
    dt.items.add(file);
    fileInput.files = dt.files;
    form.appendChild(fileInput);

    const cleanup = () => setTimeout(() => { iframe.remove(); form.remove(); }, 1000);

    document.body.appendChild(iframe);
    document.body.appendChild(form);
    // Let the blank iframe finish its own initial load before submitting, so the next
    // load event really is FormSubmit's response.
    setTimeout(() => {
      const timer = setTimeout(() => { cleanup(); reject(new Error("Upload timed out")); }, 45000);
      iframe.addEventListener("load", () => { clearTimeout(timer); cleanup(); resolve(); }, { once: true });
      form.submit();
    }, 150);
  });
}

const itemLines = (items = []) =>
  items.length ? items.map((i) => `${i.name} (${i.code}) × ${i.qty}`).join("\n") : "";

export function sendEnquiry(form, items = []) {
  const fields = baseFields(`New Sepla enquiry — ${form.company || form.name}`, form);
  const list = itemLines(items);
  if (list) fields["Enquiry list"] = list;
  fields.Page = window.location.href;
  return postJson(fields);
}

export function sendOemRequest(form, { base, finish, qty, file }) {
  const fields = baseFields(`New custom / OEM request — ${form.company || form.name}`, form);
  fields["Base product"] = base ? `${base.name} (${base.code})` : "Any product";
  fields.Finish = finish || "Flexible";
  fields["Estimated quantity"] = qty;
  fields.Artwork = file ? `${file.name} (attached)` : "None";
  fields.Page = window.location.href;
  if (file) {
    if (file.size > 9 * 1024 * 1024) {
      return Promise.reject(new Error("Artwork file is too large (max 9 MB). Please send it on WhatsApp."));
    }
    return postMultipart(fields, file);
  }
  return postJson(fields);
}
